import React, { useState, useEffect, FormEvent } from "react";
import { useParams } from "react-router-dom";
import UploadImage from "../../components/token/UploadImage";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../utils/firebase";
import { notify } from "../../utils/notify";
import NavBar from "../../components/feature/NavBar";
import Footer from "../../components/feature/Footer";


interface CompanyData {
  name: string;
  description: string;
  websiteURL: string;
  logoURL: string[];
}

const AdminCompanyModify: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [companyName, setCompanyName] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [companyWebsiteURL, setCompanyWebsiteURL] = useState("");
  const [logoPreviewURL, setLogoPreviewURL] = useState<string[]>([]);
  const [logoFile, setLogoFile] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch the company data from Firestore and prepopulate the form.
  useEffect(() => {
    if (!id) return;
    const fetchCompany = async () => {
      try {
        const companyDoc = await getDoc(doc(db, "companies", id));
        if (companyDoc.exists()) {
          const data = companyDoc.data() as CompanyData;
          setCompanyName(data.name);
          setCompanyDescription(data.description);
          setCompanyWebsiteURL(data.websiteURL);
          setLogoPreviewURL(data.logoURL || []);
        } else {
          notify({ isSuccess: false, message: "Company not found." });
        }
      } catch (error) {
        console.error("Error fetching company:", error);
        notify({ isSuccess: false, message: "Error fetching company data." });
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [id]);

  const handleSubmitCompany = async (e: FormEvent) => {
    e.preventDefault();
    try {
      // Upload new logo images if any have been added.
      let updatedLogoURLs = logoPreviewURL;
      if (logoFile.length > 0) {
        const uploadPromises = logoFile.map(async (file) => {
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = ref(storage, `logos/${fileName}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
        updatedLogoURLs = await Promise.all(uploadPromises);
      }
      const companyDocRef = doc(db, "companies", id!);
      await updateDoc(companyDocRef, {
        name: companyName,
        description: companyDescription,
        websiteURL: companyWebsiteURL,
        logoURL: updatedLogoURLs,
      });
      notify({ isSuccess: true, message: "Company updated successfully." });
    } catch (error) {
      console.error("Error updating company:", error);
      notify({ isSuccess: false, message: "An error occurred while updating the company." });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading company data...</p>
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <div className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Admin - Modify Company</h1>
        <form className="space-y-6" onSubmit={handleSubmitCompany}>
          {/* Logo Upload */}
          <UploadImage
            logoFile={logoFile}
            setLogoFile={setLogoFile}
            logoPreviewURL={logoPreviewURL}
            setLogoPreviewURL={setLogoPreviewURL}
            name="logo"
          />
          {/* Name */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Name:</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name..."
            />
          </div>
          {/* Description */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Description:</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              placeholder="Enter company description..."
            />
          </div>
          {/* Website URL */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Website:</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={companyWebsiteURL}
              onChange={(e) => setCompanyWebsiteURL(e.target.value)}
              placeholder="Enter company website URL..."
            />
          </div>
          {/* Submit Button */}
          <button
            type="submit"
            className="bg-neutral-950 text-white font-medium px-6 py-2 rounded-md"
          >
            Update Company
          </button>
        </form>
      </div>
      <Footer />
    </>
  );
};

export default AdminCompanyModify;
